(() => {
    if (customElements.get('card-gallery')) return;

    const WIDTHS = [165, 360, 533, 720, 940, 1066];
    const SWIPE_DURATION = 250; // ms, mobile slide animation
    const SWIPE_THRESHOLD = 0.2; // share of the image width a swipe must travel to change image
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const galleryRequests = new Map();

    const sizedUrl = (url, width) => url.replace(/([?&])width=\d+/, `$1width=${width}`);
    const afterNextPaint = () => new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));

    // Resolves once an image has loaded (or failed), so a slide is never shown blank.
    const imageReady = (image) =>
      !image || (image.complete && image.naturalWidth)
        ? Promise.resolve()
        : new Promise((resolve) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
          });

    // One request per gallery URL, shared by swatch hover-prefetch and click.
    function fetchGallery(url) {
      if (!galleryRequests.has(url)) {
        const request = fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error(`card-gallery ${response.status}`);
            return response.text();
          })
          .catch((error) => {
            galleryRequests.delete(url);
            throw error;
          });
        galleryRequests.set(url, request);
      }
      return galleryRequests.get(url);
    }

    class CardGallery extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.reset();
        // Hover is tracked on the whole image area, so moving onto Quick View, the badge or the
        // wishlist heart (which sit on top of the gallery) doesn't count as leaving the image.
        this.hoverArea = this.closest('.tov-card__media') || this;
        this.hoverArea.addEventListener('pointerenter', this.onPointerEnter);
        this.hoverArea.addEventListener('pointerleave', this.onPointerLeave);
        this.addEventListener('focusin', this.hydrate);
        this.addEventListener('pointerdown', this.onPointerDown);
        // Capture phase: runs before the slide link's own click, so a swipe can cancel it.
        this.addEventListener('click', this.onClick, true);
      }

      reset() {
        this.track = this.querySelector('[data-track]');
        this.added = 0;
        this.current = 0;
        this.target = 0;
        this.drag = null;
        const slides = this.slides;
        if (slides.length && !slides.some((slide) => slide.classList.contains('is-active'))) {
          slides[0].classList.add('is-active');
        }
      }

      get slides() {
        return this.track ? Array.from(this.track.children) : [];
      }

      get urls() {
        return (this.dataset.slides || '').split(',').filter(Boolean);
      }

      get secondImage() {
        const slide = this.slides[1];
        return slide ? slide.querySelector('img') : null;
      }

      loadSecondImage() {
        const image = this.secondImage;
        if (image && image.loading === 'lazy') image.loading = 'eager';
      }

      // Adds slides 3-5 from data-slides (once).
      addSlides() {
        if (!this.track) return;
        const urls = this.urls;
        let html = '';
        while (this.added < urls.length) {
          const url = urls[this.added];
          const srcset = WIDTHS.map((width) => `${sizedUrl(url, width)} ${width}w`).join(', ');
          html +=
            `<a class="card-gallery__slide" href="${this.dataset.url}" tabindex="-1">` +
            `<img src="${sizedUrl(url, 533)}" srcset="${srcset}" sizes="${this.dataset.sizes}" alt="" decoding="async">` +
            `</a>`;
          this.added += 1;
        }
        if (html) this.track.insertAdjacentHTML('beforeend', html);
      }

      hydrate = () => {
        this.loadSecondImage();
        this.addSlides();
      };

      // Desktop: crossfade to an image once it has loaded. A newer call replaces a pending one.
      async go(index, input) {
        const token = (this.goToken = (this.goToken || 0) + 1);
        const slides = this.slides;
        if (slides.length < 2) return;
        const target = (index + slides.length) % slides.length;
        this.target = target;
        if (target === this.current) return;
        await imageReady(slides[target].querySelector('img'));
        if (token !== this.goToken) return;
        this.show(target, input);
      }

      // Makes an image the visible one and reports it, e.g. for a Noibu custom event:
      // document.addEventListener('card:slide', (e) => { ... e.detail ... });
      show(index, input) {
        const slides = this.slides;
        if (slides[this.current]) slides[this.current].classList.remove('is-active');
        slides[index].classList.add('is-active');
        this.current = index;
        this.target = index;
        this.dispatchEvent(
          new CustomEvent('card:slide', {
            bubbles: true,
            detail: { productId: this.dataset.productId, index, total: slides.length, input },
          })
        );
      }

      // Every hover shows image 2 (once it has loaded) and starts downloading images 3-5.
      onPointerEnter = (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hydrate();
        if (this.current === 0) this.go(1, 'hover');
      };

      // Always back to image 1 when the mouse leaves the image area (also cancels a pending hover).
      onPointerLeave = (event) => {
        if (event.pointerType !== 'mouse') return;
        this.go(0, 'hover');
      };

      onClick = (event) => {
        if (this.suppressClick) {
          // The finger was swiping, not tapping: don't open the product.
          this.suppressClick = false;
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        const arrow = event.target.closest('[data-gallery-step]');
        if (!arrow) return;
        event.preventDefault();
        event.stopPropagation();
        this.hydrate();
        this.go(this.target + Number(arrow.dataset.galleryStep), 'arrow');
      };

      // --- Mobile swipe: the image follows the finger, then moves exactly one image ---------
      onPointerDown = (event) => {
        this.suppressClick = false;
        if (event.pointerType === 'mouse' || !event.isPrimary || event.target.closest('[data-gallery-step]')) return;
        this.hydrate();
        if (this.slides.length < 2 || this.settling) return;
        this.drag = {
          id: event.pointerId,
          x: event.clientX,
          dx: 0,
          moved: false,
          width: this.track.getBoundingClientRect().width,
        };
        this.addEventListener('pointermove', this.onPointerMove);
        this.addEventListener('pointerup', this.onPointerUp);
        this.addEventListener('pointercancel', this.onPointerCancel);
      };

      onPointerMove = (event) => {
        const drag = this.drag;
        if (!drag || event.pointerId !== drag.id) return;
        let dx = event.clientX - drag.x;
        if (!drag.moved) {
          if (Math.abs(dx) < 8) return;
          drag.moved = true;
          this.track.classList.add('is-dragging');
        }
        const neighbour = dx < 0 ? this.current + 1 : this.current - 1;
        const hasNeighbour = neighbour >= 0 && neighbour < this.slides.length;
        if (!hasNeighbour) dx /= 3; // resistance at the first and last image
        drag.dx = dx;
        this.place(hasNeighbour ? neighbour : -1, dx, drag.width);
      };

      // Positions the current image at dx and the neighbouring image right next to it.
      place(neighbour, dx, width) {
        this.slides.forEach((slide, i) => {
          if (i === this.current) {
            slide.style.transform = `translateX(${dx}px)`;
          } else if (i === neighbour) {
            slide.classList.add('is-peek');
            slide.style.transform = `translateX(${dx + (i > this.current ? width : -width)}px)`;
          } else {
            slide.classList.remove('is-peek');
            slide.style.transform = '';
          }
        });
      }

      onPointerUp = (event) => {
        const drag = this.drag;
        if (!drag || event.pointerId !== drag.id) return;
        this.endDrag();
        if (!drag.moved) return; // a tap: let the link open the product
        this.suppressClick = true;
        let target = this.current;
        if (Math.abs(drag.dx) > drag.width * SWIPE_THRESHOLD) target += drag.dx < 0 ? 1 : -1;
        if (target < 0 || target >= this.slides.length) target = this.current;
        this.settle(target, drag.width);
      };

      // The browser took over (e.g. the gesture became a vertical page scroll): snap back.
      onPointerCancel = (event) => {
        const drag = this.drag;
        if (!drag || event.pointerId !== drag.id) return;
        this.endDrag();
        if (drag.moved) this.settle(this.current, drag.width);
      };

      endDrag() {
        this.drag = null;
        this.removeEventListener('pointermove', this.onPointerMove);
        this.removeEventListener('pointerup', this.onPointerUp);
        this.removeEventListener('pointercancel', this.onPointerCancel);
      }

      // Finishes a swipe: slides to the new image, or back to the current one.
      settle(target, width) {
        const slides = this.slides;
        const from = this.current;
        const peek = slides.find((slide) => slide.classList.contains('is-peek'));
        const duration = reducedMotion.matches ? 0 : SWIPE_DURATION;
        this.settling = true;
        this.track.classList.remove('is-dragging');
        this.track.classList.add('is-settling');

        const move = (slide, x) => {
          if (!slide) return;
          slide.style.transition = duration ? `transform ${duration}ms ease` : 'none';
          slide.style.transform = `translateX(${x}px)`;
        };
        if (target !== from) {
          move(slides[from], target > from ? -width : width);
          move(slides[target], 0);
        } else {
          move(slides[from], 0);
          if (peek) move(peek, slides.indexOf(peek) > from ? width : -width);
        }

        setTimeout(() => {
          if (target !== from) this.show(target, 'swipe');
          slides.forEach((slide) => {
            slide.classList.remove('is-peek');
            slide.style.transition = '';
            slide.style.transform = '';
          });
          void this.track.offsetWidth; // apply the instant switch before the fade transition returns
          this.track.classList.remove('is-settling');
          this.settling = false;
        }, duration);
      }

      // Swatch click: replace this gallery with the one rendered by the card-gallery section at `url`.
      async load(url) {
        const token = (this.loadToken = (this.loadToken || 0) + 1);
        let html;
        try {
          html = await fetchGallery(url);
        } catch (error) {
          return 'error';
        }

        // A prefetched response resolves before the browser paints the tap; wait one frame so
        // the swatch's active state paints first and this work stays out of INP.
        await afterNextPaint();
        if (token !== this.loadToken) return 'stale';

        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('card-gallery');
        if (!next) return 'error';

        // Keep the current image on screen until the new colour's first image is decoded.
        const nextImage = next.querySelector('img');
        if (nextImage) {
          const preload = new Image();
          preload.sizes = nextImage.getAttribute('sizes') || '';
          preload.srcset = nextImage.getAttribute('srcset') || '';
          preload.src = nextImage.getAttribute('src');
          await preload.decode().catch(() => {});
          if (token !== this.loadToken) return 'stale';
        }

        this.dataset.slides = next.dataset.slides || '';
        this.dataset.url = next.dataset.url || '';
        this.innerHTML = next.innerHTML;
        this.reset();
        // The user is engaged with this card: load the new colour's image 2 and images 3-5 now.
        this.hydrate();
        return 'ok';
      }
    }

    class ProductCard extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.addEventListener('click', this.onClick);
        this.addEventListener('pointerover', this.onIntent);
        this.addEventListener('focusin', this.onIntent);
      }

      // Start fetching a colour's gallery as soon as the pointer or focus reaches its swatch.
      onIntent = (event) => {
        const swatch = event.target.closest('[data-card-swatch]');
        if (swatch && swatch.dataset.galleryUrl) fetchGallery(swatch.dataset.galleryUrl).catch(() => {});
      };

      onClick = async (event) => {
        const swatch = event.target.closest('[data-card-swatch]');
        if (!swatch || event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
        event.preventDefault();
        event.stopPropagation();
        if (swatch.getAttribute('aria-current') === 'true') return;

        // Visible feedback first, in the same frame as the tap.
        this.querySelectorAll('[data-card-swatch][aria-current]').forEach((item) =>
          item.removeAttribute('aria-current')
        );
        swatch.setAttribute('aria-current', 'true');
        this.querySelectorAll('[data-card-link]').forEach((link) => {
          link.href = swatch.href;
        });
        this.querySelectorAll('[data-product-url]').forEach((button) => {
          button.dataset.productUrl = swatch.href;
        });
        const gallery = this.querySelector('card-gallery');
        if (!gallery) return;
        const result = await gallery.load(swatch.dataset.galleryUrl);
        if (result === 'error') window.location.assign(swatch.href);
      };
    }

    customElements.define('card-gallery', CardGallery);
    customElements.define('product-card', ProductCard);
  })();